/**
 * Failure containment (§9.3): "media elements sit in an error boundary per block; a failed load
 * renders the placeholder + retry. A failed asset never breaks its card, table row, or the
 * Profile render."
 *
 * `resetKey` lets the retry action remount the subtree: bump the key, the boundary clears.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

export type ErrorBoundaryProps = {
  children: ReactNode;
  /** Rendered instead of the children once a render below has thrown. */
  fallback: (retry: () => void) => ReactNode;
  /** Changing this value clears a captured error (used by "Retry"). */
  resetKey?: unknown;
};

type ErrorBoundaryState = { failed: boolean };

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true };
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Contained on purpose: a broken block must not take the screen with it.
    console.error('Render failed inside an error boundary', error, info.componentStack);
  }

  private readonly retry = (): void => {
    this.setState({ failed: false });
  };

  override render(): ReactNode {
    if (this.state.failed) return this.props.fallback(this.retry);
    return this.props.children;
  }
}

export default ErrorBoundary;
